import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, Activity, TrendingUp, ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react'
import { useAuth } from '../hooks/useSupabaseAuth.jsx'
import UsageService from '../services/usageService'
import LoadingSkeleton from '../components/LoadingSkeleton'
import AuthPrompt from '../components/AuthPrompt'

const UsagePage = () => {
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const [usageData, setUsageData] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 200,
    hasNextPage: false,
    hasPrevPage: false
  })

  // Load usage data
  const loadUsageData = async (page = 1) => {
    if (!user) return
    
    setLoading(true)
    setError(null)
    
    try {
      const result = await UsageService.getUsageHistory(user.id, page, 200)
      
      if (result.success) {
        setUsageData(result.data)
        setPagination(result.pagination)
        setCurrentPage(page)
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError('Gagal memuat data usage')
      console.error('Error loading usage data:', err)
    } finally {
      setLoading(false)
    }
  }

  // Load stats
  const loadStats = async () => {
    if (!user) return
    
    try {
      const result = await UsageService.getUsageStats(user.id)
      
      if (result.success) {
        setStats(result.data)
      }
    } catch (err) {
      console.error('Error loading stats:', err)
    }
  }

  useEffect(() => {
    if (user) {
      loadUsageData(1)
      loadStats()
    }
  }, [user])

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadUsageData(newPage)
    }
  }

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Format credits
  const formatCredits = (credits) => {
    return credits.toLocaleString('id-ID')
  }

  // Show loading state during auth
  if (authLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSkeleton />
      </div>
    )
  }

  // Show auth prompt if not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center px-4">
        <div className="text-center max-w-md w-full">
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <img 
              src="https://media.cara.id/images/logo-cara-white.png" 
              alt="Cara Logo" 
              className="h-16"
            />
          </div>
          
          {/* Main Message */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 mb-6">
            <h2 className="text-2xl font-bold text-white mb-3">Akses Riwayat Penggunaan</h2>
            <p className="text-gray-400 mb-6">
              Silakan login terlebih dahulu untuk melihat riwayat penggunaan dan aktivitas Anda.
            </p>
            
            <button
              onClick={() => {
                // Dispatch custom event to open auth modal
                window.dispatchEvent(new CustomEvent('openAuthModal'))
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              Login Sekarang
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
          
          {/* Additional Info */}
          <p className="text-sm text-gray-500">
            Belum punya akun? Login dengan Google untuk membuat akun baru.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-bg-primary text-text-primary">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border-primary bg-bg-secondary px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors flex-shrink-0"
              title="Kembali ke Home"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl font-semibold text-text-primary truncate">Riwayat Penggunaan</h1>
              <p className="text-xs sm:text-sm text-text-tertiary truncate">Detail penggunaan agent dan fitur</p>
            </div>
          </div>
          
          {/* Stats Cards */}
          {stats && (
            <div className="hidden md:flex items-center space-x-4">
              <div className="bg-bg-tertiary rounded-lg px-3 py-2">
                <div className="flex items-center space-x-2">
                  <Activity className="w-4 h-4 text-blue-400" />
                  <div>
                    <p className="text-xs text-text-tertiary">Total Aktivitas</p>
                    <p className="text-sm font-medium">{stats.totalActivities}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-bg-tertiary rounded-lg px-3 py-2">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <div>
                    <p className="text-xs text-text-tertiary">Total Credits</p>
                    <p className="text-sm font-medium">{formatCredits(stats.totalCreditsUsed)}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-bg-tertiary rounded-lg px-3 py-2">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-purple-400" />
                  <div>
                    <p className="text-xs text-text-tertiary">30 Hari Terakhir</p>
                    <p className="text-sm font-medium">{formatCredits(stats.recentCreditsUsed)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Stats */}
      {stats && (
        <div className="md:hidden flex-shrink-0 bg-bg-secondary border-b border-border-primary px-3 py-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-bg-tertiary rounded-lg px-2 py-2 text-center min-w-0">
              <Activity className="w-4 h-4 text-blue-400 mx-auto mb-1" />
              <p className="text-xs text-text-tertiary truncate">Aktivitas</p>
              <p className="text-xs sm:text-sm font-medium truncate">{stats.totalActivities}</p>
            </div>
            
            <div className="bg-bg-tertiary rounded-lg px-2 py-2 text-center min-w-0">
              <TrendingUp className="w-4 h-4 text-green-400 mx-auto mb-1" />
              <p className="text-xs text-text-tertiary truncate">Credits</p>
              <p className="text-xs sm:text-sm font-medium truncate">{formatCredits(stats.totalCreditsUsed)}</p>
            </div>
            
            <div className="bg-bg-tertiary rounded-lg px-2 py-2 text-center min-w-0">
              <Calendar className="w-4 h-4 text-purple-400 mx-auto mb-1" />
              <p className="text-xs text-text-tertiary truncate">30 Hari</p>
              <p className="text-xs sm:text-sm font-medium truncate">{formatCredits(stats.recentCreditsUsed)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="p-3 sm:p-4">
            <LoadingSkeleton />
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center max-w-sm mx-auto">
              <BarChart3 className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">Error</h3>
              <p className="text-text-tertiary mb-4 text-sm">{error}</p>
              <button
                onClick={() => loadUsageData(currentPage)}
                className="px-4 py-2 bg-brand-primary hover:bg-brand-secondary rounded-lg text-white transition-colors text-sm"
              >
                Coba Lagi
              </button>
            </div>
          </div>
        ) : usageData.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center max-w-sm mx-auto">
              <BarChart3 className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">Belum Ada Data</h3>
              <p className="text-text-tertiary mb-4 text-sm">Anda belum memiliki riwayat penggunaan.</p>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-brand-primary hover:bg-brand-secondary rounded-lg text-white transition-colors text-sm"
              >
                Mulai Menggunakan Agent
              </button>
            </div>
          </div>
        ) : (
          <div className="p-3 sm:p-4">
            {/* Table */}
            <div className="bg-bg-secondary rounded-lg border border-border-primary overflow-hidden">
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-bg-tertiary">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                        Waktu
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                        Agent/Fitur
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                        Judul/Query
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-text-tertiary uppercase tracking-wider">
                        Credits
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-text-tertiary uppercase tracking-wider">
                        Sisa Credits
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-primary">
                    {usageData.map((item, index) => (
                      <tr key={item.id} className="hover:bg-bg-tertiary/50 transition-colors">
                        <td className="px-4 py-3 text-sm text-text-secondary">
                          {formatDate(item.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {UsageService.formatActionType(item.action_type)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-primary max-w-xs">
                          <div className="truncate" title={item.title}>
                            {item.title}
                          </div>
                          {item.query && item.query !== '-' && (
                            <div className="text-xs text-text-tertiary truncate mt-1" title={item.query}>
                              {item.query}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-red-400">
                          -{formatCredits(item.credits_used)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-text-secondary">
                          {formatCredits(item.credits_remaining)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden">
                {usageData.map((item, index) => (
                  <div key={item.id} className="p-3 border-b border-border-primary last:border-b-0">
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0">
                        {UsageService.formatActionType(item.action_type)}
                      </span>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-medium text-red-400">-{formatCredits(item.credits_used)}</div>
                        <div className="text-xs text-text-tertiary">Sisa: {formatCredits(item.credits_remaining)}</div>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <div className="text-sm text-text-primary font-medium break-words" title={item.title}>
                        {item.title}
                      </div>
                      {item.query && item.query !== '-' && (
                        <div className="text-xs text-text-tertiary break-words mt-1" title={item.query}>
                          {item.query}
                        </div>
                      )}
                    </div>
                    
                    <div className="text-xs text-text-tertiary">
                      {formatDate(item.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-4 sm:mt-6">
                {/* Mobile Pagination Info */}
                <div className="text-xs sm:text-sm text-text-tertiary text-center mb-3 sm:hidden">
                  {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} - {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} dari {pagination.totalItems}
                </div>
                
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                  {/* Desktop Pagination Info */}
                  <div className="hidden sm:block text-sm text-text-tertiary">
                    Menampilkan {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} - {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} dari {pagination.totalItems} data
                  </div>
                  
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className="p-1.5 sm:p-2 rounded-lg border border-border-primary hover:bg-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    
                    <div className="flex items-center space-x-1">
                       {/* Page numbers - responsive */}
                       {Array.from({ length: Math.min(3, pagination.totalPages) }, (_, i) => {
                         let pageNum
                         if (pagination.totalPages <= 3) {
                           pageNum = i + 1
                         } else if (pagination.currentPage <= 2) {
                           pageNum = i + 1
                         } else if (pagination.currentPage >= pagination.totalPages - 1) {
                           pageNum = pagination.totalPages - 2 + i
                         } else {
                           pageNum = pagination.currentPage - 1 + i
                         }
                         
                         return (
                           <button
                             key={pageNum}
                             onClick={() => handlePageChange(pageNum)}
                             className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm transition-colors min-w-[28px] sm:min-w-[32px] ${
                               pageNum === pagination.currentPage
                                 ? 'bg-brand-primary text-white'
                                 : 'border border-border-primary hover:bg-bg-tertiary'
                             }`}
                           >
                             {pageNum}
                           </button>
                         )
                       })}
                       
                       {/* Additional pages for desktop */}
                       <div className="hidden sm:flex items-center space-x-1">
                         {Array.from({ length: Math.min(2, Math.max(0, pagination.totalPages - 3)) }, (_, i) => {
                           let pageNum
                           if (pagination.totalPages <= 5) {
                             pageNum = 4 + i
                           } else if (pagination.currentPage <= 3) {
                             pageNum = 4 + i
                           } else if (pagination.currentPage >= pagination.totalPages - 2) {
                             pageNum = pagination.totalPages - 1 + i
                           } else {
                             pageNum = pagination.currentPage + 2 + i
                           }
                           
                           if (pageNum <= pagination.totalPages) {
                             return (
                               <button
                                 key={pageNum}
                                 onClick={() => handlePageChange(pageNum)}
                                 className={`px-3 py-1 rounded-lg text-sm transition-colors min-w-[32px] ${
                                   pageNum === pagination.currentPage
                                     ? 'bg-brand-primary text-white'
                                     : 'border border-border-primary hover:bg-bg-tertiary'
                                 }`}
                               >
                                 {pageNum}
                               </button>
                             )
                           }
                           return null
                         })}
                       </div>
                     </div>
                    
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="p-1.5 sm:p-2 rounded-lg border border-border-primary hover:bg-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default UsagePage